import React from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface Props {
  /** The dialog's accessible name. */
  label: string;
  children: React.ReactNode;
}

/**
 * A modal dialog the keyboard cannot leave: focus moves inside when it opens, Tab and Shift+Tab cycle
 * within it, and everything else on the page is inert (unreachable and hidden from assistive technology)
 * until it closes, when focus returns to where it was. Rendered inside the modal's portal, so it mounts
 * together with the dialog's content.
 */
const DialogFocusTrap: React.FC<Props> = ({ label, children }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const container = ref.current;
    if (!container) return undefined;

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusables = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
    (focusables()[0] ?? container).focus();

    // The page behind the dialog: every top-level element except the portal that holds it.
    const background = Array.from(document.body.children).filter(
      (element): element is HTMLElement => element instanceof HTMLElement && !element.contains(container) && !element.hasAttribute('inert'),
    );
    const previousAriaHidden = background.map(element => element.getAttribute('aria-hidden'));
    background.forEach(element => {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !container.contains(active);
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      background.forEach((element, index) => {
        element.removeAttribute('inert');
        const ariaHidden = previousAriaHidden[index];
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      previouslyFocused?.focus();
    };
  }, []);

  return (
    <div ref={ref} role='dialog' aria-modal='true' aria-label={label} tabIndex={-1}>
      {children}
    </div>
  );
};

export default DialogFocusTrap;
